// presentation/hooks/useIncidente.ts
import { useCallback, useState } from 'react';
import { container } from '@/di/container';
import { Incidente } from '@/domain/entities';
import {
  CreateIncidenteDTO,
  IncidentesParamsDTO,
  ChangeIncidenteDTO,
  UpdateIncidenteDTO,
} from '@/application/dto';
import {
  CreateIncidenteUseCase,
  GetIncidenteByIdUseCase,
  PatchIncidenteUseCase,
  UpdateIncidenteUseCase,
} from '@/application/use-cases';
import { GetIncidentesUseCase } from '@/application/use-cases/incidente/GetIncidentesUseCase';

export function useIncidente() {
  const [incidente, setIncidente] =
    useState<Incidente | null>(null);

  const [data, setData] =
    useState<Incidente[]>([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState<string>();

  const clearError = () => {
    setError(undefined);
  };

  const loadById = useCallback(
    async (id: number | string) => {
      try {
        setLoading(true);
        setError(undefined);

        const result = await container
          .get<GetIncidenteByIdUseCase>(
            'GetIncidenteByIdUseCase'
          )
          .execute(String(id));

        setIncidente(result);

        return result;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Error al cargar el incidente';

        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadAll = useCallback(
    async (
      params: IncidentesParamsDTO
    ) => {
      try {
        setLoading(true);
        setError(undefined);

        const result = await container
          .get<GetIncidentesUseCase>(
            'GetIncidentesUseCase'
          )
          .execute(params);

        setData(result);

        return result;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Error al cargar los incidentes';

        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const create = useCallback(
    async (data: CreateIncidenteDTO) => {
      try {
        setLoading(true);
        setError(undefined);

        const result = await container
          .get<CreateIncidenteUseCase>(
            'CreateIncidenteUseCase'
          )
          .execute(data);

        return result;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Error al crear el incidente';

        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const update = useCallback(
    async (
      id: string,
      data: UpdateIncidenteDTO
    ) => {
      try {
        setLoading(true);
        setError(undefined);

        const result = await container
          .get<UpdateIncidenteUseCase>(
            'UpdateIncidenteUseCase'
          )
          .execute(id, data);

        setIncidente(result);

        setData((current) =>
          current.map((item) =>
            String(item.id) === String(id)
              ? result
              : item
          )
        );

        return result;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Error al actualizar el incidente';

        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );
  const changeEstado = useCallback(
  async (
    id: string,
    data: ChangeIncidenteDTO
  ) => {
    try {
      setLoading(true);
      setError(undefined);

      const result = await container
        .get<PatchIncidenteUseCase>(
          'PatchIncidenteUseCase'
        )
        .execute(id, data);

      setIncidente(result);

      setData((current) =>
        current.map((item) =>
          String(item.id) === id
            ? result
            : item
        )
      );

      return result;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Error al cambiar el estado';

      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  },
  []
);

  return {
    incidente,
    data,
    loading,
    error,
    loadById,
    loadAll,
    create,
    update,
    clearError,
    changeEstado
  };
}